import { useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";
import { ROOM } from "../../lib/layout";
import { day } from "../../lib/daylight";

const XB = ROOM.W / 2;
const ZB = ROOM.D / 2;
// corners only, clear of the arches, the windows and the walking paths
export const URNS = [
  { x: -XB + 0.75, z: -ZB + 0.75 },
  { x: XB - 0.75, z: -ZB + 0.75 },
  { x: -XB + 0.75, z: ZB - 0.75 },
  { x: XB - 0.6, z: ZB - 0.85 },
];
const SWAGS = [
  ...[-2, 2].flatMap((z) => [{ p: [-XB + 0.03, 4.45, z], r: Math.PI / 2 }, { p: [XB - 0.03, 4.45, z], r: -Math.PI / 2 }]),
  ...[5.7, 8.1].map((x) => ({ p: [x, 4.6, ZB - 0.03], r: Math.PI })),
];
const LANTERNS = [-2, 2].flatMap((z) => [{ p: [-XB + 0.12, 2.5, z], r: Math.PI / 2, f: 1 }, { p: [XB - 0.12, 2.5, z], r: -Math.PI / 2, f: -1 }]);

const glowFrag = `uniform float uLamp; uniform float uTime; varying vec2 vUv;
void main(){ float d = length(vUv - 0.5) * 2.0; float a = exp(-d * d * 3.5) * (0.25 + 0.6 * uLamp) * (0.94 + 0.06 * sin(uTime * 2.3));
  gl_FragColor = vec4(vec3(1.0, 0.75, 0.42) * a, 1.0); }`;
const glowVert = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

function Urn({ x, z, tex }) {
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.4, toneMapped: false }), [tex]);
  const ref = useMemo(() => ({ m: null }), []);
  const { camera } = useThree();
  useFrame(() => ref.m && (ref.m.rotation.y = Math.atan2(camera.position.x - x, camera.position.z - z)));
  return (
    <mesh ref={(m) => (ref.m = m)} position={[x, 0.68, z]} material={mat}>
      <planeGeometry args={[0.95, 1.38]} />
    </mesh>
  );
}

export function HallDecor() {
  const [urn, green, lantern] = useTexture(["/hall/urn.webp", "/hall/greenery.webp", "/hall/lantern.webp"]);
  [urn, green, lantern].forEach((t) => (t.colorSpace = THREE.SRGBColorSpace));
  const greenMat = useMemo(() => new THREE.MeshBasicMaterial({ map: green, transparent: true, alphaTest: 0.35, toneMapped: false, side: THREE.DoubleSide }), [green]);
  const lanternMat = useMemo(() => new THREE.MeshBasicMaterial({ map: lantern, transparent: true, alphaTest: 0.4, toneMapped: false }), [lantern]);
  const glowMat = useMemo(() => new THREE.ShaderMaterial({ vertexShader: glowVert, fragmentShader: glowFrag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, uniforms: { uLamp: day.uLamp, uTime: { value: 0 } } }), []);
  useFrame(() => (glowMat.uniforms.uTime.value = day.uMotion.value ? performance.now() / 1000 : 0));
  return (
    <group data-testid="hall-decor">
      {URNS.map((u, i) => (
        <Urn key={i} {...u} tex={urn} />
      ))}
      {SWAGS.map((s, i) => (
        <mesh key={i} position={s.p} rotation={[0, s.r, 0]} material={greenMat}>
          <planeGeometry args={[2.2, 1.43]} />
        </mesh>
      ))}
      {LANTERNS.map((l, i) => (
        <group key={i} position={l.p} rotation={[0, l.r, 0]}>
          <mesh material={lanternMat} scale={[l.f, 1, 1]}>
            <planeGeometry args={[0.42, 0.6]} />
          </mesh>
          <mesh position={[0, -0.08, 0.02]} material={glowMat}>
            <planeGeometry args={[1.3, 1.3]} />
          </mesh>
        </group>
      ))}
    </group>
  );
}
